import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import * as codemirror from 'codemirror';
import 'codemirror/lib/codemirror.css';
import {
    assign,
    escapeHtmlSpecialChar,
    find,
    forEach,
    get,
    includes,
    isEmpty,
    isNumber,
    map
} from '@libs/shared';
import { FIELD_TYPE_MAP, Intl } from '../../constants';
import 'codemirror/addon/display/placeholder';
import 'codemirror/addon/display/autorefresh';
import { Button, PlusOutlined, Popover, Tooltip } from '@fx-ui/fine-design';
import classNames from 'classnames';
import FieldSelectList from '../../components/field-select-list';
import { DEFAULT_FIELD_REGEX } from './constants';
import { checkRichTextLimit } from './utils';

export interface CustomField {
    className?: string;
    text: string;
    value: string;
    fieldType: string;
    id: string;
    hasAffix?: boolean;
}

export interface CustomFieldRichTextProps {
    fields: CustomField[];
    className?: string;
    style?: React.CSSProperties;
    isMultiLine?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    value: string;
    onChange?: (v: string) => void;
    renderAddBtn?: (allowAdd: boolean) => ReactElement;
    isShowAddBtn?: boolean;
    mirrorOption?: Record<string, any>;
    addBtnText?: string;
    maxFieldCount?: number;
    maxTextLength?: number;
    fieldRegexp?: RegExp;
    fieldTypeMap?: Record<string, any>;
    styleMap?: Record<string, any>;
    showInvalidField?: boolean; // 显示无效字段
    invalidFieldText?: string;
    isStatusError?: boolean; // 错误输入状态
    onCustomFieldSelectorRender?: (params: {
        onChange: (fieldValue: string) => void;
    }) => React.ReactNode; // 自定义字段选择
}

const getMap = (fields: Record<string, any>) => {
    const fieldMap: Record<string, Record<string, any>> = {};
    forEach(fields, (field) => {
        fieldMap[field.value] = field;
    });
    return fieldMap;
};

interface CodeMirrorRef extends CodeMirror {
    editor: codemirror.Editor;
}

/**
 * 富文本编辑器 适配自定义字段业务
 * 字段匹配规则受控
 *
 * @param props CustomFieldRichTextProps
 * @class
 */
const CustomFieldRichText: React.FC<CustomFieldRichTextProps> = (
    props: CustomFieldRichTextProps
) => {
    const {
        fields,
        value,
        onChange,
        renderAddBtn,
        addBtnText,
        isShowAddBtn = true,
        mirrorOption = {},
        isMultiLine,
        className,
        style,
        readonly,
        disabled = false,
        maxFieldCount,
        maxTextLength,
        fieldRegexp = DEFAULT_FIELD_REGEX,
        fieldTypeMap = FIELD_TYPE_MAP,
        styleMap,
        showInvalidField,
        invalidFieldText,
        isStatusError,
        onCustomFieldSelectorRender
    } = props;

    const mirrorRef = useRef<CodeMirrorRef>(null);
    const [allowAdd, setAllowAdd] = useState(true);
    const [textLimit, setTextLimit] = useState(false);
    const [isFocus, setIsFocus] = useState(false);

    const fieldMap = getMap(fields);
    const fieldValueList = map(fields, (field) => {
        return find(field.value.split(fieldRegexp), (v) => {
            return !isEmpty(v);
        });
    });

    useEffect(() => {
        if (mirrorRef.current) {
            setTemplate(value);
        }
    }, [value]);

    // string转dom元素
    const parseDom = (arg: any) => {
        const objE = document.createElement('div');
        objE.innerHTML = arg;
        return objE.childNodes;
    };
    /**
     * 插入字段
     * override
     *
     * @param widgetName ${widgetName}
     * @param needFocus 聚焦
     * @param isInvalidField 是否为无效字段
     */
    const addField = (widgetName: string, needFocus?: boolean, isInvalidField?: boolean) => {
        const { editor } = mirrorRef.current || {};
        if (!editor) {
            return;
        }
        const field = fieldMap[widgetName];
        let label = get(field, 'text') || '';
        const hasAffix = get(field, 'hasAffix', false);
        const className = get(field, 'className', '');
        if (isInvalidField) {
            label = invalidFieldText;
        }
        if (isEmpty(label)) {
            return;
        }
        const defaultPosition: codemirror.Position = { ch: 0, line: 0 };
        const from = editor.getCursor('from') || defaultPosition;
        const fieldValue = find(fields, (v) => {
            return widgetName === v.value;
        });
        if (fieldValue && hasAffix) {
            const affix = get(styleMap, [fieldTypeMap[fieldValue.fieldType], 'text'], '');
            label = `${label}(${affix})`;
        }
        editor.replaceSelection(label);
        const to = editor.getCursor() || defaultPosition;
        const extraClass = isInvalidField ? 'invalid-field' : 'field-item';
        editor.markText(from, to, {
            handleMouseEvents: true,
            atomic: true,
            replacedWith: parseDom(
                `<span id='${widgetName}' class='cm-field ${className} ${extraClass}'>${escapeHtmlSpecialChar(
                    label
                )}</span>`
            )[0] as HTMLElement
        });
        if (needFocus) {
            editor.focus();
        }
    };

    /**
     * 设置模板
     *
     * @param value any
     */
    const setTemplate = (value: any) => {
        const { editor } = mirrorRef.current || {};
        if (!editor) {
            return;
        }
        const cursor = editor.getCursor();
        editor.setValue('');
        if (isEmpty(value)) {
            return;
        }
        const matchWidgetName = (v: string) => {
            return v.match(fieldRegexp);
        };
        if (matchWidgetName(value)) {
            // 有插入字段 ${ }
            const matchItems = value.split(fieldRegexp);
            forEach(matchItems, (item: string) => {
                if (!item) {
                    return;
                }
                if (includes(fieldValueList, item.trim())) {
                    // 字段
                    addField(item.trim());
                } else if (showInvalidField && matchWidgetName(item.trim())) {
                    // 无效字段
                    addField(item.trim(), false, true);
                } else {
                    // 普通文本
                    editor.replaceSelection(item);
                }
            });
        } else {
            // 没有插入字段
            editor.replaceSelection(value);
        }

        editor.refresh();
        cursor && editor.setCursor(cursor);
    };

    /**
     * 设置编辑器内容
     */
    const getTemplate = () => {
        const { editor } = mirrorRef.current || {};
        if (!editor) {
            return '';
        }

        // 获取所有的行
        const doc = editor.getDoc();
        const lineCount = doc.lineCount();
        const template: string[] = [];

        // 遍历每一行
        for (let i = 0; i < lineCount; i++) {
            const line = doc.getLine(i);
            const lineStart = { line: i, ch: 0 };
            const lineEnd = { line: i, ch: line.length };
            const marks = doc.findMarks(lineStart, lineEnd);

            // 每一行中的mark都排序
            marks.sort((a, b) => {
                const aPos = a.find();
                const bPos = b.find();
                if (aPos && 'from' in aPos && bPos && 'from' in bPos) {
                    return aPos.from.ch - bPos.from.ch;
                }
                return 0;
            });

            let lineText = line;
            // 从后往前替换，这样不会影响位置
            for (let j = marks.length - 1; j >= 0; j--) {
                const mark = marks[j];
                if (!mark) {
                    continue;
                }
                const pos = mark.find();
                if (pos && 'from' in pos && 'to' in pos) {
                    const from = pos.from;
                    const to = pos.to;
                    const markElement = mark.replacedWith as HTMLElement;
                    if (markElement && markElement.id) {
                        // 替换文本内容为字段ID
                        lineText =
                            lineText.substring(0, from.ch) +
                            markElement.id +
                            lineText.substring(to.ch);
                    }
                }
            }
            template.push(lineText);
        }

        return template.join('\n').trim();
    };

    const tokenBase = (stream: any) => {
        if (stream.eatSpace()) {
            return null;
        }
        stream.next();
        return null;
    };

    const handleUpdate = () => {
        if (!isNumber(maxFieldCount) && !isNumber(maxTextLength)) {
            return;
        }
        const template = getTemplate();
        const { isFieldMax, isTextValid } = checkRichTextLimit(template, {
            FIELD: maxFieldCount,
            TEXT: maxTextLength
        });
        // 文字数限制
        setTextLimit(!isTextValid);
        // 允许添加字段按钮
        setAllowAdd(isFieldMax);
    };

    const renderFieldSelector = () => {
        if (onCustomFieldSelectorRender) {
            return onCustomFieldSelectorRender({
                onChange: (fieldValue) => {
                    addField(fieldValue, true);
                }
            });
        }

        return (
            <FieldSelectList
                styleMap={styleMap}
                fieldTypeMap={fieldTypeMap}
                fieldItems={map(fields, (field) => {
                    return {
                        ...field,
                        name: field.value,
                        type: field.fieldType
                    };
                })}
                onFieldSelect={(comboItem) => {
                    addField(comboItem.name, true);
                }}
            />
        );
    };

    const renderInsideAddBtn = () => {
        if (isShowAddBtn) {
            if (allowAdd) {
                return (
                    <div
                        className={classNames('text-title-container', {
                            'text-title-container-inside': !isMultiLine
                        })}
                    >
                        <Popover
                            trigger="click"
                            arrow={false}
                            hasPadding={false}
                            placement="bottom-left"
                            style={{ width: 240 }}
                            content={renderFieldSelector()}
                        >
                            {renderAddBtn?.(allowAdd) || (
                                <div
                                    className={classNames('field-select-btn', {
                                        'field-select-btn-inside': isMultiLine
                                    })}
                                >
                                    <Tooltip
                                        effect="light"
                                        trigger="hover"
                                        arrow={true}
                                        placement="top"
                                        content={addBtnText || Intl.get('field.member.insert')}
                                    >
                                        <Button
                                            disabled={!allowAdd}
                                            icon={<PlusOutlined />}
                                            type="text"
                                        />
                                    </Tooltip>
                                </div>
                            )}
                        </Popover>
                    </div>
                );
            } else {
                return (
                    <div
                        className={classNames('text-title-container', {
                            'text-title-container-inside': !isMultiLine
                        })}
                    >
                        {renderAddBtn?.(allowAdd) || (
                            <div
                                className={classNames('field-select-btn', {
                                    'field-select-btn-inside': isMultiLine
                                })}
                            >
                                <Button disabled={!allowAdd} icon={<PlusOutlined />} type="text" />
                            </div>
                        )}
                    </div>
                );
            }
        }
    };

    if (disabled) {
        return (
            <div className={classNames('fx-custom-field-rich-text', className)} style={style}>
                <div className="rich-text-empty">{Intl.get('content.empty')}</div>
            </div>
        );
    }

    const renderErrorMsg = () => {
        if (textLimit && isFocus) {
            return (
                <div className="error-msg">
                    {Intl.get('max.length.error.message', { max: maxTextLength! })}
                </div>
            );
        }
    };

    return (
        <>
            <div
                className={classNames(
                    'fx-custom-field-rich-text',
                    { 'rich-text-single': !isMultiLine },
                    { 'is-error': isStatusError || textLimit },
                    className
                )}
                style={style}
            >
                {renderErrorMsg()}
                <div className="main-container">
                    {renderInsideAddBtn()}
                    <CodeMirror
                        ref={mirrorRef}
                        className={
                            isMultiLine ? 'code-mirror-multi-line' : 'code-mirror-single-line'
                        }
                        onUpdate={handleUpdate}
                        onFocus={() => {
                            setIsFocus(true);
                        }}
                        onBlur={() => {
                            setIsFocus(false);
                            onChange && onChange(getTemplate());
                        }}
                        options={assign({}, mirrorOption, {
                            autoRefresh: true,
                            lineWrapping: isMultiLine,
                            readOnly: readonly,
                            scrollbarStyle: isMultiLine ? 'native' : 'null'
                        })}
                        defineMode={{
                            name: 'label',
                            fn: () => {
                                return {
                                    startState: () => {
                                        return { tokens: [] };
                                    },
                                    token: (stream: any, state: any) => {
                                        return (state.tokens[0] || tokenBase)(stream, state);
                                    },
                                    fold: 'brace'
                                };
                            }
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default CustomFieldRichText;
